import type { SideEvent, TurnEvent } from "../types";
import { rootSessionId } from "./waste";

export interface CacheStats {
  turns: number;
  input: number;
  cacheRead: number;
  cacheWrite: number;
  /** cacheRead / (cacheRead + cacheWrite + input); null when no input at all. */
  hitRate: number | null;
}

export function cacheStats(turns: Iterable<TurnEvent>): CacheStats {
  const s: CacheStats = { turns: 0, input: 0, cacheRead: 0, cacheWrite: 0, hitRate: null };
  for (const t of turns) {
    s.turns++;
    s.input += t.usage.input;
    s.cacheRead += t.usage.cacheRead;
    s.cacheWrite += t.usage.cacheWrite;
  }
  const denom = s.input + s.cacheRead + s.cacheWrite;
  s.hitRate = denom > 0 ? s.cacheRead / denom : null;
  return s;
}

export function cacheByProject(turns: TurnEvent[]): Map<string, CacheStats> {
  return groupStats(turns, (t) => t.projectPath || "(unknown)");
}

export function cacheBySession(turns: TurnEvent[]): Map<string, CacheStats> {
  return groupStats(turns, rootSessionId);
}

function groupStats(turns: TurnEvent[], key: (t: TurnEvent) => string): Map<string, CacheStats> {
  const groups = new Map<string, TurnEvent[]>();
  for (const t of turns) {
    const k = key(t);
    const list = groups.get(k);
    if (list) list.push(t);
    else groups.set(k, [t]);
  }
  const out = new Map<string, CacheStats>();
  for (const [k, list] of groups) out.set(k, cacheStats(list));
  return out;
}

export interface CompactionSummary {
  total: number;
  bySession: Map<string, number>;
  /** Tokens reported dropped by compaction (pre - post), summed. */
  tokensCompacted: number;
}

export function summarizeCompactions(events: SideEvent[]): CompactionSummary {
  const out: CompactionSummary = { total: 0, bySession: new Map(), tokensCompacted: 0 };
  for (const e of events) {
    if (e.kind !== "compact_boundary") continue;
    out.total++;
    out.bySession.set(e.sessionId, (out.bySession.get(e.sessionId) ?? 0) + 1);
    const pre = typeof e.data.preTokens === "number" ? e.data.preTokens : 0;
    const post = typeof e.data.postTokens === "number" ? e.data.postTokens : 0;
    if (pre > post) out.tokensCompacted += pre - post;
  }
  return out;
}
